import React, { useState } from 'react';
import { ExclamationTriangleIcon, SparklesIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import PaymentMethodModal from './PaymentMethodModal';
import { isAndroid } from '../utils/deviceDetection';

interface UpgradeOptionsModalProps {
  isOpen?: boolean;
  title?: string;
  message: string;
  feature?: string;
  onClose?: () => void;
  showOneDayPass?: boolean;
  showPremium?: boolean;
  icon?: 'warning' | 'info' | 'lock';
  oneDayPassBenefits?: string[];
  premiumBenefits?: string[];
  customPremiumPrice?: string;
}

const UpgradeOptionsModal: React.FC<UpgradeOptionsModalProps> = ({ 
  isOpen = true,
  title = 'Upgrade Necessário',
  message, 
  feature, 
  onClose,
  showOneDayPass = true,
  showPremium = true,
  icon = 'warning',
  oneDayPassBenefits,
  premiumBenefits,
  customPremiumPrice
}) => {
  const navigate = useNavigate();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const isAndroidDevice = isAndroid();

  // PIX Code from the user's request
  const PIX_CODE = '00020126580014br.gov.bcb.pix01360300e793-8b58-41ff-be85-65af61c651c2520400005303986540515.905802BR5901N6001C62170513upmindydayuse6304F74F';

  // Always show Premium option regardless of device
  const shouldShowPremium = showPremium;

  if (!isOpen) return null;

  const icons = {
    warning: ExclamationTriangleIcon,
    info: SparklesIcon,
    lock: ExclamationTriangleIcon,
  };

  const IconComponent = icons[icon];

  const iconColors = {
    warning: 'text-amber-400',
    info: 'text-blue-400',
    lock: 'text-red-400',
  };

  const defaultOneDayPassBenefits = [
    'Acesso completo por 24 horas',
    'Decks e cards ilimitados',
    'Todos os recursos premium',
    'Perfeito para estudar intensivamente'
  ];

  const defaultPremiumBenefits = [
    'Acesso ilimitado permanente',
    'Todos os recursos premium',
    'Suporte prioritário',
    'Melhor custo-benefício'
  ];

  const handlePremiumUpgrade = () => {
    navigate('/pricing');
    if (onClose) onClose();
  };

  const handleOneDayPass = () => {
    setShowPaymentModal(true);
  };

  return (
    <>
      {/* Payment Method Modal */}
      <PaymentMethodModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        pixCode={PIX_CODE}
      />

      {/* Main Upgrade Options Modal */}
      <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-lg w-full mx-auto border border-gray-200 dark:border-gray-700 shadow-xl max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex items-start min-w-0 flex-1">
              <div className="flex-shrink-0">
                <IconComponent className={`h-6 w-6 sm:h-7 sm:w-7 ${iconColors[icon]}`} aria-hidden="true" />
              </div>
              <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white leading-tight">
                  {title}
                </h3>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <div className="mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              {message}
            </p>
            
            {(showOneDayPass || shouldShowPremium) && (
              <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2 sm:mb-3">
                Escolha uma opção para continuar:
              </p>
            )}

            {/* One-Day Pass Option */}
            {showOneDayPass && (
              <div className="mb-3 sm:mb-4 p-3 sm:p-4 border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center min-w-0 flex-1">
                    <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                      Passe de 1 Dia
                    </h4>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 leading-tight whitespace-nowrap">R$ 15,90</p>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">pagamento único</p>
                  </div>
                </div>
                <ul className="text-sm sm:text-base text-gray-600 dark:text-gray-400 space-y-0.5 sm:space-y-1 mb-2 sm:mb-3">
                  {(oneDayPassBenefits || defaultOneDayPassBenefits).map((benefit, index) => (
                    <li key={index} className="leading-snug">• {benefit}</li>
                  ))}
                </ul>
                <button
                  onClick={handleOneDayPass}
                  className="w-full inline-flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                  Comprar Passe de 1 Dia
                </button>
              </div>
            )}

            {/* Premium Upgrade Option - Hidden for Android */}
            {shouldShowPremium && (
              <div className="p-3 sm:p-4 border-2 border-primary-200 dark:border-primary-800 rounded-lg bg-gradient-to-br from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center min-w-0 flex-1">
                    <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                      Premium
                    </h4>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg sm:text-2xl font-bold text-primary-600 dark:text-primary-400 leading-tight whitespace-nowrap">
                      {customPremiumPrice || 'A partir de'}
                    </p>
                    {!customPremiumPrice && (
                      <p className="text-lg sm:text-2xl font-bold text-primary-600 dark:text-primary-400 leading-tight">R$ 24,92</p>
                    )}
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">por mês</p>
                  </div>
                </div>
                <ul className="text-sm sm:text-base text-gray-600 dark:text-gray-400 space-y-0.5 sm:space-y-1 mb-2 sm:mb-3">
                  {(premiumBenefits || defaultPremiumBenefits).map((benefit, index) => (
                    <li key={index} className="leading-snug">• {benefit}</li>
                  ))}
                </ul>
                <button
                  onClick={handlePremiumUpgrade}
                  className="w-full inline-flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                  Ver Planos Premium
                </button>
              </div>
            )}
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="w-full inline-flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 text-sm sm:text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Mais Tarde
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default UpgradeOptionsModal;
